import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RouteIcon from "@mui/icons-material/Route";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import NearMeIcon from "@mui/icons-material/NearMe";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DownloadIcon from "@mui/icons-material/Download";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import TutorialVideo from "./TutorialVideo";
import { useTranslation } from "../../../hooks/useTranslation";
import { sanitizeRichText } from "../../../utils/sanitizeRichText";

interface TutorialsModalProps {
    open: boolean;
    onClose: () => void;
}

const TutorialsModal: React.FC<TutorialsModalProps> = ({
    open,
    onClose,
}) => {
    const { p } = useTranslation("tutorials");
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h6" fontWeight={600}>
                    {p("title")}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ marginLeft: 'auto', color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                    <TutorialVideo />
                    <Box sx={{ marginTop: '40px', mb: 2, pt: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <NewReleasesIcon color="primary" />
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, margin: '0 !important' }}>
                            {p("new_features")}
                        </Typography>
                    </Box>
                    <List sx={{ pt: 0 }}>
                        {/* Resource Uploads */}
                        <Divider variant="inset" sx={{ ml: 7 }} />

                        <ListItem alignItems="flex-start" sx={{ px: 0, mb: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <CloudUploadIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("uploads_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("uploads_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Auto Route */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, my: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <RouteIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("auto_route_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("auto_route_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Enhanced Team Permissions */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, my: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <GroupAddIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("permissions_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("permissions_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Dynamic Live Map */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, my: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <NearMeIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("live_map_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("live_map_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Report Tab */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, my: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <AssessmentIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("report_tab_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("report_tab_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Instant Downloads */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, my: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <DownloadIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("downloads_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("downloads_body")) }} />
                                }
                            />
                        </ListItem>

                        <Divider variant="inset" component="li" sx={{ ml: 7 }} />

                        {/* Ease of Use */}
                        <ListItem alignItems="flex-start" sx={{ px: 0, mt: 2 }}>
                            <ListItemIcon sx={{ mt: 0.5 }}>
                                <TouchAppIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography fontWeight={600}>{p("ease_title")}</Typography>}
                                secondary={
                                    <Typography variant="body2" color="text.primary" component="div"
                                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p("ease_body")) }} />
                                }
                            />
                        </ListItem>
                    </List>
                </Box>
            </DialogContent>

            {/* <DialogActions sx={{ p: 3, backgroundColor: 'rgba(0,0,0,0.02)', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="body2" color="text.secondary">
                        Need more help? Check out our full guide.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    href="https://www.connectyid.com/how-it-works"
                    target="_blank"
                    sx={{ 
                        px: 4, 
                        py: 1.5, 
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: 3
                    }}
                    fullWidth={false}
                >
                    Visit Full How-It-Works Guide
                </Button>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
            </DialogActions> */}
        </Dialog>
    );
};

export default TutorialsModal;
